import { TextField, Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentMethodOption } from '../custom-types/PaymentMethodOption';
import { fetchPaymentMethodOptionList } from '../features/home/HomeActions';
import { selectPaymentMethodOptionList } from '../features/home/HomeSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      '&.selectEmptyMissing .MuiOutlinedInput-notchedOutline': {
        border: '1px solid',
        borderColor: theme.palette.error.main,
      },
      '&.selectEmptyMissing label': {
        color: theme.palette.error.main,
      },
      '& .Mui-disabled': {
        backgroundColor: theme.palette.uxGrey.hover,
      },
    },
    listContainer: {
      listStyle: 'none',
      position: 'absolute',
      background: '#FFFFFF',
      overflowY: 'auto',
      overflowX: 'hidden',
      zIndex: 5,
      maxHeight: '200px',
      marginTop: 1,
      borderRadius: '4px',
      boxShadow: '0px 3px 3px -5px rgb(0 0 0 / 20%), 0px 6px 8px -1px rgb(0 0 0 / 14%), 0px 1px 12px 0px rgb(0 0 0 / 12%);',
      padding: theme.spacing(0.5, 0),
    },
    listMargin: {
      marginTop: '-22px',
    },
    listItem: {
      padding: '6px 16px 6px 16px',
      letterSpacing: '0.00938em',
      lineHeight: 1.5,
      fontSize: '0.875rem',
      overflow: 'hidden',
      width: '100%',
      cursor: 'pointer',
    },
    listItemSelected: {
      background: theme.palette.uxGrey.focus,
    },
    optionContainer: {
      width: '100%',
    },
    optionDisplayName: {
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    optionDetail: {
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }),
);

interface PaymentMethodAutoCompleteProps {
  fieldValue: string;
  setFieldValue: (fieldValue: string) => void;
  paymentMethodOption: PaymentMethodOption | undefined;
  setPaymentMethodOption: (option: PaymentMethodOption | undefined) => void;
  resourceId: string;
}

const PaymentMethodAutoComplete: React.FC<PaymentMethodAutoCompleteProps> = props => {
  const { fieldValue, setFieldValue, paymentMethodOption, setPaymentMethodOption, resourceId } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const paymentMethodOptionList = useSelector(selectPaymentMethodOptionList);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [invalidFieldValue, setInvalidFieldValue] = useState(false);

  useEffect(() => {
    if (paymentMethodOptionList && paymentMethodOptionList.length > 0) {
      setSelectedIndex(0);
    } else if (!paymentMethodOptionList || paymentMethodOptionList.length === 0) {
      setSelectedIndex(-1);
    }
  }, [paymentMethodOptionList]);

  const buildDisplayText = (option: PaymentMethodOption) => option.displayName || '';

  const handleValueSelect = (selectIndex?: number) => {
    const idx = selectIndex !== undefined ? selectIndex : selectedIndex;
    if (paymentMethodOptionList && idx > -1 && idx < paymentMethodOptionList.length) {
      const selected = paymentMethodOptionList[idx];
      setPaymentMethodOption(selected);
      setFieldValue(buildDisplayText(selected));
      setSelectedIndex(-1);
      setAnchorEl(null);
      setInvalidFieldValue(false);
    }
  };

  const handleListItemHover = (index: number) => {
    setSelectedIndex(index);
  };

  const handleIndexUpdate = (increment: boolean) => {
    if (paymentMethodOptionList && paymentMethodOptionList.length > 0) {
      let updatedIndex = selectedIndex;
      if (increment) {
        updatedIndex = selectedIndex === paymentMethodOptionList.length - 1 ? 0 : updatedIndex + 1;
      } else {
        updatedIndex = selectedIndex < 1 ? paymentMethodOptionList.length - 1 : updatedIndex - 1;
      }
      setSelectedIndex(updatedIndex);

      const selectedElement = document.getElementById(`payment-method-list-auto-complete-${updatedIndex}`);
      const listElement = document.getElementById(`payment-method-list-auto-complete`);
      if (selectedElement && listElement) {
        if (selectedElement.offsetTop < listElement.scrollTop) {
          listElement.scrollTop = selectedElement.offsetTop;
        } else if (selectedElement.offsetTop > listElement.scrollTop + listElement.clientHeight - selectedElement.clientHeight) {
          listElement.scrollTop = selectedElement.offsetTop - (listElement.clientHeight - selectedElement.clientHeight);
        }
      }
    }
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value) {
      if (value.length >= 4 && resourceId) {
        dispatch(fetchPaymentMethodOptionList(value, resourceId));
        setAnchorEl(event.currentTarget);
      } else {
        setAnchorEl(null);
      }
    } else {
      setAnchorEl(null);
    }
    setFieldValue(value);
    setSelectedIndex(-1);
    if (invalidFieldValue) setInvalidFieldValue(false);
  };

  const handleFieldFocus = () => {
    setSelectedIndex(-1);
    setAnchorEl(null);
  };

  const handleListBlur = () => {
    setAnchorEl(null);
    setSelectedIndex(-1);
    const trimmedValue = fieldValue.trim();
    if (trimmedValue) {
      const existingDisplay = paymentMethodOption ? buildDisplayText(paymentMethodOption) : '';
      if (fieldValue !== existingDisplay) {
        const matched = paymentMethodOptionList ? paymentMethodOptionList.find(o => buildDisplayText(o) === trimmedValue) : undefined;
        if (matched) {
          setPaymentMethodOption(matched);
          setInvalidFieldValue(false);
        } else {
          setPaymentMethodOption(undefined);
          setInvalidFieldValue(true);
        }
      }
    } else {
      setFieldValue('');
      setPaymentMethodOption(undefined);
      setInvalidFieldValue(false);
    }
  };

  const handleFieldBlur = (event: React.FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLInputElement;
    if (!relatedTarget || relatedTarget.getAttribute('id') !== 'payment-method-list-auto-complete') {
      handleListBlur();
    }
    if (!paymentMethodOption) {
      setFieldValue('');
      setPaymentMethodOption(undefined);
      setInvalidFieldValue(false);
    }
  };

  const handleFieldKeyDown = (event: React.KeyboardEvent<HTMLDivElement | HTMLUListElement>) => {
    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
        handleIndexUpdate(true);
        break;
      case 'Up':
      case 'ArrowUp':
        handleIndexUpdate(false);
        break;
      case 'Enter':
        handleValueSelect();
        break;
      default:
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement | HTMLUListElement>) => {
    event.preventDefault();
    handleFieldKeyDown(event);
  };

  return (
    <>
      <TextField
        id="payment-method-search-id"
        fullWidth
        variant="outlined"
        label="Email, Holder Name or Last 4"
        inputProps={{
          'aria-label': 'payment method search',
          'aria-describedby': [
            invalidFieldValue ? 'payment-method-helper-text-id' : undefined,
            selectedIndex > -1 ? `payment-method-list-auto-complete-${selectedIndex}` : 'payment-method-list-empty-option',
          ]
            .filter(Boolean)
            .join(' '),
        }}
        value={fieldValue}
        className={classes.textField}
        autoComplete="nope"
        onChange={handleFieldChange}
        onFocus={handleFieldFocus}
        onBlur={handleFieldBlur}
        onKeyDown={handleFieldKeyDown}
        error={invalidFieldValue}
        helperText={invalidFieldValue ? 'Invalid Payment Method' : undefined}
        FormHelperTextProps={{ id: 'payment-method-helper-text-id' }}
        data-cy="payment-method-search"
      />
      {anchorEl !== null && (
        <ul
          id="payment-method-list-auto-complete"
          className={`${classes.listContainer} ${invalidFieldValue ? classes.listMargin : undefined}`}
          style={{ width: anchorEl.offsetWidth }}
          tabIndex={1}
          onBlur={handleListBlur}
          onKeyDown={handleListKeyDown}
        >
          {paymentMethodOptionList &&
            paymentMethodOptionList.map((option, index) => (
              <li
                id={`payment-method-list-auto-complete-${index}`}
                key={index}
                className={`${classes.listItem} ${index === selectedIndex ? classes.listItemSelected : ''}`}
                onMouseOver={() => handleListItemHover(index)}
                onMouseDown={() => handleValueSelect(index)}
              >
                <div className={classes.optionContainer}>
                  <div className={classes.optionDisplayName}>{option.displayName}</div>
                  {option.email && <div className={classes.optionDetail}>Email: {option.email}</div>}
                  {option.holderName && <div className={classes.optionDetail}>Name: {option.holderName}</div>}
                  {option.postalCode && <div className={classes.optionDetail}>Zip: {option.postalCode}</div>}
                </div>
              </li>
            ))}
          {paymentMethodOptionList && paymentMethodOptionList.length === 0 && (
            <li id="payment-method-list-empty-option" className={classes.listItem}>
              No payment methods found.
            </li>
          )}
        </ul>
      )}
    </>
  );
};

export default PaymentMethodAutoComplete;
